import React, { useState, useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import { ArrowRight, ArrowLeft, Mail, ShieldCheck, KeyRound, RefreshCw } from 'lucide-react';
import { login } from '../../store/slices/authSlice';
import { RootState } from '../../store';
import { authService } from '../../services/authService';
import toast from 'react-hot-toast';
import logo from '../../assets/DWBRANDprview.png';

const UserLoginForm: React.FC = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();

  const { token, user } = useSelector((state: RootState) => state.auth);

  // Check query param for redirect
  const searchParams = new URLSearchParams(location.search);
  const redirectUrl = searchParams.get('redirect');

  const [email, setEmail] = useState('');
  const [otpValues, setOtpValues] = useState<string[]>(['', '', '', '']);
  const [otpStep, setOtpStep] = useState<'email' | 'otp'>('email');
  const [loading, setLoading] = useState(false);
  const [resendTimer, setResendTimer] = useState(0);

  const otpInputRefs = [
    useRef<HTMLInputElement>(null),
    useRef<HTMLInputElement>(null),
    useRef<HTMLInputElement>(null),
    useRef<HTMLInputElement>(null),
  ];

  // Redirect if already logged in as regular USER
  useEffect(() => {
    if (token && user) {
      if (user.role === 'USER') {
        navigate(redirectUrl || '/user/profile', { replace: true });
      }
      // If user is Admin (AU / SU) or Dealer, DO NOT auto-redirect away so they can log in as a regular User!
    }
  }, [token, user, navigate, redirectUrl]);

  // Resend Timer Countdown
  useEffect(() => {
    let interval: NodeJS.Timeout;
    if (resendTimer > 0) {
      interval = setInterval(() => {
        setResendTimer((prev) => prev - 1);
      }, 1000);
    }
    return () => clearInterval(interval);
  }, [resendTimer]);

  // Handle Send OTP
  const handleSendOTP = async (e: React.FormEvent) => {
    e.preventDefault();
    const cleanEmail = email.trim().toLowerCase();
    if (!cleanEmail || !cleanEmail.includes('@')) {
      toast.error('Please enter a valid email address');
      return;
    }

    setLoading(true);
    try {
      const response = await authService.sendOTP(cleanEmail);

      if (response.success && (response.status === 200 || response.statusCode === 200)) {
        toast.success(`Verification OTP sent to ${cleanEmail}`);
        setOtpStep('otp');
        setResendTimer(30);
        setTimeout(() => otpInputRefs[0].current?.focus(), 150);
      } else {
        toast.error(response.message || 'Failed to send OTP');
      }
    } catch (error: any) {
      toast.error(error?.data?.message || error?.message || 'Failed to send OTP code. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // Handle Single Digit OTP Input
  const handleDigitChange = (index: number, value: string) => {
    if (!/^\d*$/.test(value)) return;
    const newOtp = [...otpValues];
    newOtp[index] = value.slice(-1);
    setOtpValues(newOtp);

    // Auto-focus next input
    if (value && index < 3) {
      otpInputRefs[index + 1].current?.focus();
    }
  };

  const handleKeyDown = (index: number, e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Backspace' && !otpValues[index] && index > 0) {
      otpInputRefs[index - 1].current?.focus();
    }
  };

  const handlePaste = (e: React.ClipboardEvent<HTMLInputElement>) => {
    e.preventDefault();
    const pastedData = e.clipboardData.getData('text').trim().slice(0, 4);
    if (/^\d{4}$/.test(pastedData)) {
      const digits = pastedData.split('');
      setOtpValues(digits);
      otpInputRefs[3].current?.focus();
    }
  };

  // Handle OTP Submit
  const handleVerifyOTP = async (e: React.FormEvent) => {
    e.preventDefault();
    const fullOtp = otpValues.join('');
    if (fullOtp.length !== 4) {
      toast.error('Please enter the full 4-digit OTP code');
      return;
    }

    setLoading(true);
    try {
      const cleanEmail = email.trim().toLowerCase();
      const response = await authService.verifyOTP(cleanEmail, fullOtp);

      if (response.success && (response.status === 200 || response.statusCode === 200)) {
        const authToken = response.data || '';
        let userName = cleanEmail.split('@')[0];
        let userId: string | number | undefined = undefined;

        try {
          const tokenParts = authToken.split('.');
          if (tokenParts.length === 3) {
            const payload = JSON.parse(atob(tokenParts[1]));
            userId = payload.userId;
          }
        } catch {
          // ignore decode error
        }

        if (!authToken) {
          toast.error('Login failed: Token missing from response');
          return;
        }

        dispatch(
          login({
            user: {
              email: cleanEmail,
              role: 'USER',
              name: userName,
              id: userId,
            },
            token: authToken,
          })
        );

        toast.success('Welcome back! Login successful 🎉');

        // Redirect appropriately
        if (redirectUrl) {
          navigate(redirectUrl, { replace: true });
        } else {
          navigate('/user/profile', { replace: true });
        }
      } else {
        toast.error(response.message || 'Invalid OTP code. Please try again.');
      }
    } catch (error: any) {
      toast.error(error?.data?.message || error?.message || 'Invalid OTP code. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-blue-950 to-gray-900 flex items-center justify-center p-4 sm:p-6 relative overflow-hidden">
      {/* Dynamic Background Accents */}
      <div className="absolute -top-32 -left-32 w-96 h-96 bg-blue-600/20 rounded-full blur-3xl pointer-events-none" />
      <div className="absolute -bottom-32 -right-32 w-96 h-96 bg-emerald-600/20 rounded-full blur-3xl pointer-events-none" />

      <div className="w-full max-w-md relative z-10">
        {/* Top Header Controls */}
        <div className="flex justify-between items-center mb-6">
          <button
            onClick={() => {
              if (redirectUrl) {
                navigate(redirectUrl);
              } else if (window.history.length > 1) {
                navigate(-1);
              } else {
                navigate('/');
              }
            }}
            className="inline-flex items-center gap-2 text-sm font-medium text-gray-300 hover:text-white transition-colors bg-white/10 px-3 py-1.5 rounded-xl backdrop-blur-xs border border-white/10"
          >
            <ArrowLeft className="w-4 h-4" /> Back
          </button>

          <Link
            to="/login"
            className="text-xs font-semibold text-blue-400 hover:text-blue-300 transition-colors bg-blue-500/10 px-3 py-1.5 rounded-xl border border-blue-500/20"
          >
            Admin Portal &rarr;
          </Link>
        </div>

        {/* Card Container */}
        <div className="bg-white/95 backdrop-blur-xl rounded-3xl p-6 sm:p-8 shadow-2xl border border-white/20 relative">
          {/* Logo & Portal Info */}
          <div className="text-center mb-6">
            <Link to="/" className="inline-flex items-center justify-center gap-3 mb-3">
              <div className="w-12 h-10 rounded-xl bg-gradient-to-br from-blue-600 to-blue-700 shadow-md flex items-center justify-center">
                <img src={logo} alt="Diasporawise" className="h-6 w-7" />
              </div>
              <div className="text-left">
                <h1 className="text-xl font-bold tracking-tight text-gray-900" style={{ fontFamily: 'Montserrat, sans-serif' }}>
                  DIASPORA<span className="text-blue-600">WISE</span>
                </h1>
                <span className="inline-block px-2 py-0.5 rounded-md bg-blue-100 text-blue-800 text-[10px] font-extrabold uppercase tracking-wider">
                  User Login
                </span>
              </div>
            </Link>
            <h2 className="text-2xl font-bold text-gray-900 tracking-tight">User Sign In</h2>
            <p className="text-xs text-gray-500 mt-1">Instant passwordless sign in via Email OTP</p>
          </div>

          {/* Admin Session Notice */}
          {user && (user.role === 'AU' || user.role === 'SU') && (
            <div className="mb-6 bg-amber-50 border border-amber-200 rounded-2xl p-3.5 text-xs text-amber-900 flex items-center gap-2 shadow-2xs">
              <KeyRound className="w-4 h-4 text-amber-600 shrink-0" />
              <span>You are currently signed in as Admin (<strong>{user.email}</strong>). Verifying OTP below will switch your session to a User account.</span>
            </div>
          )}

          {/* Step 1: Email Form */}
          {otpStep === 'email' ? (
            <form onSubmit={handleSendOTP} className="space-y-4">
              <div>
                <label className="block text-xs font-bold text-gray-700 uppercase tracking-wider mb-1.5">
                  User Email Address
                </label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3.5 flex items-center pointer-events-none">
                    <Mail className="h-4 h-4 text-gray-400" />
                  </div>
                  <input
                    type="email"
                    required
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="Enter your email address"
                    className="w-full pl-10 pr-4 py-3 border border-gray-200 rounded-xl text-sm font-medium text-gray-900 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white transition-all shadow-2xs"
                  />
                </div>
              </div>

              <button
                type="submit"
                disabled={loading}
                className="w-full py-3.5 px-4 bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 text-white text-sm font-extrabold rounded-xl shadow-md transition-all flex items-center justify-center gap-2 disabled:opacity-50"
              >
                {loading ? (
                  <>
                    <RefreshCw className="w-4 h-4 animate-spin" /> Sending Verification Code...
                  </>
                ) : (
                  <>
                    <span>Send Verification OTP</span>
                    <ArrowRight className="w-4 h-4" />
                  </>
                )}
              </button>
            </form>
          ) : (
            /* Step 2: 4-Digit OTP Form */
            <form onSubmit={handleVerifyOTP} className="space-y-5">
              <div className="text-center bg-blue-50/70 p-3 rounded-2xl border border-blue-100">
                <p className="text-xs text-blue-900 font-medium">
                  We sent a 4-digit code to <strong className="font-bold">{email}</strong>
                </p>
                <button
                  type="button"
                  onClick={() => {
                    setOtpStep('email');
                    setOtpValues(['', '', '', '']);
                  }}
                  className="text-[11px] text-blue-600 hover:text-blue-800 font-bold underline mt-1"
                >
                  Change email address
                </button>
              </div>

              <div>
                <label className="block text-xs font-bold text-gray-700 uppercase tracking-wider mb-2 text-center">
                  Enter 4-Digit OTP Code
                </label>
                <div className="flex justify-center gap-3" onPaste={handlePaste}>
                  {otpValues.map((digit, idx) => (
                    <input
                      key={idx}
                      ref={otpInputRefs[idx]}
                      type="text"
                      inputMode="numeric"
                      maxLength={1}
                      value={digit}
                      onChange={(e) => handleDigitChange(idx, e.target.value)}
                      onKeyDown={(e) => handleKeyDown(idx, e)}
                      className="w-14 h-14 text-center text-2xl font-extrabold text-gray-900 bg-white border-2 border-gray-200 rounded-2xl focus:border-blue-600 focus:ring-4 focus:ring-blue-100 transition-all shadow-2xs"
                    />
                  ))}
                </div>
              </div>

              <div className="flex items-center justify-between text-xs pt-1">
                <button
                  type="button"
                  disabled={resendTimer > 0 || loading}
                  onClick={(e) => void handleSendOTP(e)}
                  className="text-blue-600 hover:text-blue-800 font-bold disabled:opacity-50 flex items-center gap-1"
                >
                  <RefreshCw className={`w-3.5 h-3.5 ${resendTimer > 0 ? 'animate-spin' : ''}`} />
                  {resendTimer > 0 ? `Resend Code in ${resendTimer}s` : 'Resend Code'}
                </button>
              </div>

              <div className="flex gap-3 pt-2">
                <button
                  type="button"
                  onClick={() => {
                    setOtpStep('email');
                    setOtpValues(['', '', '', '']);
                  }}
                  className="w-1/3 py-3 px-3 bg-gray-100 hover:bg-gray-200 text-gray-700 text-xs font-bold rounded-xl transition-all"
                >
                  Back
                </button>
                <button
                  type="submit"
                  disabled={loading || otpValues.join('').length !== 4}
                  className="w-2/3 py-3 px-4 bg-gradient-to-r from-emerald-600 to-emerald-700 hover:from-emerald-700 hover:to-emerald-800 text-white text-sm font-extrabold rounded-xl shadow-md transition-all flex items-center justify-center gap-2 disabled:opacity-50"
                >
                  {loading ? (
                    <>
                      <RefreshCw className="w-4 h-4 animate-spin" /> Verifying...
                    </>
                  ) : (
                    <>
                      <span>Verify & Sign In</span>
                      <ShieldCheck className="w-4 h-4" />
                    </>
                  )}
                </button>
              </div>
            </form>
          )}
        </div>
      </div>
    </div>
  );
};

export default UserLoginForm;
